import React, { useCallback, useEffect, useState } from "react";
import { CalendarCheck, Link2, RefreshCw, Unlink, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const GCAL = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/calendar`;

export function GoogleCalendarCard() {
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`${GCAL}/status`);
      setStatus(data);
      if (data.connected) {
        api.get(`${GCAL}/calendars`).then(({ data: d }) => setCalendars(d.calendars || [])).catch(() => {});
      }
    } catch { setStatus({ configured: true, connected: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gcal");
    if (g === "connected") { toast.success("Google Calendar connected — your team events now sync automatically."); }
    else if (g === "error") { toast.error("Couldn't connect Google Calendar. Please try again."); }
    if (g) {
      params.delete("gcal");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`${GCAL}/connect`);
      window.location.href = data.authorization_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post(`${GCAL}/disconnect`);
      toast.success("Google Calendar disconnected.");
      setCalendars([]);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pickCalendar = async (id) => {
    const cal = calendars.find((c) => c.id === id);
    setStatus((s) => ({ ...s, calendar_id: id, calendar_name: cal?.name || id }));
    try {
      await api.put(`${GCAL}/selected`, { calendar_id: id, calendar_name: cal?.name || id });
      toast.success(`Syncing to "${cal?.name || id}"`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${GCAL}/test-event`);
      if (data.ok) {
        toast.success(`Test event added to "${data.calendar_name}". Check your Google Calendar.`, { duration: 8000 });
        if (data.html_link) window.open(data.html_link, "_blank", "noopener");
      } else {
        toast.error(`Sync failed on "${data.calendar_name}": ${data.error}`, { duration: 14000 });
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  if (status.configured === false) return null;

  return (
    <Card className="mb-6 border-slate-200 p-5 shadow-sm" data-testid="gcal-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${status.connected ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
            <CalendarCheck className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-slate-800 font-heading">Google Calendar sync</p>
            {status.connected ? (
              <p className="text-sm text-slate-500" data-testid="gcal-connected-email">
                Connected as <span className="font-medium text-slate-700">{status.email || "your Google account"}</span> · events sync automatically
              </p>
            ) : (
              <p className="text-sm text-slate-500">Connect once and every team event is added to your personal calendar automatically.</p>
            )}
          </div>
        </div>

        {status.connected ? (
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-400">Syncing to</span>
              <Select value={status.calendar_id} onValueChange={pickCalendar}>
                <SelectTrigger className="min-w-[180px]" data-testid="gcal-calendar-select"><SelectValue placeholder="Choose calendar" /></SelectTrigger>
                <SelectContent>
                  {calendars.length === 0 && <SelectItem value={status.calendar_id} data-testid="gcal-cal-current">{status.calendar_name}</SelectItem>}
                  {calendars.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`gcal-cal-${c.id}`}>{c.name}{c.primary ? " (default)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={sendTest} disabled={busy} data-testid="gcal-test-btn" className="rounded-full">
              <Send className="mr-2 h-4 w-4" /> Send test event
            </Button>
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy} data-testid="gcal-disconnect-btn" className="rounded-full text-red-600 hover:text-red-700">
              <Unlink className="mr-2 h-4 w-4" /> Disconnect
            </Button>
          </div>
        ) : (
          <Button onClick={connect} disabled={busy} data-testid="gcal-connect-btn" variant="outline" className="rounded-full hover:bg-blue-600 hover:text-white">
            {busy ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />} Connect Google Calendar
          </Button>
        )}
      </div>
    </Card>
  );
}

export default GoogleCalendarCard;
